
import React, { useContext, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { AuthContext } from '../contexts/auth';
import { getSignatureBalance } from '../services/api_firme';

export default function FirmeCreditsBar({
  onBuyCredits,
  onBalanceChange,
  refreshKey = 0
}) {
  const { token, profile } = useContext(AuthContext);

  const [feaCredits, setFeaCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsError, setCreditsError] = useState(false);

  const isEmployee = profile?.customer_type === 'employee';

  const isCompanyUser =
    !!profile?.the_real_user_email &&
    !!profile?.company?.email &&
    profile.the_real_user_email.trim().toLowerCase() ===
      profile.company.email.trim().toLowerCase();

  const isConsultantUser =
    profile?.customer_type === 'consultant' &&
    !!profile?.the_real_user_email &&
    !!profile?.company?.email &&
    profile.the_real_user_email.trim().toLowerCase() !==
      profile.company.email.trim().toLowerCase();

  useEffect(() => {
    if (!token) {
      setFeaCredits(null);
      setCreditsLoading(false);
      setCreditsError(false);
      return;
    }

    let active = true;
    setCreditsLoading(true);
    setCreditsError(false);

    getSignatureBalance(token)
      .then(data => {
        if (!active) return;

        const value = profile?.customer_type === 'company'
          ? data?.free
          : data?.company_credits ?? data?.free;

        const normalizedValue =
        value != null && Number.isFinite(Number(value))
            ? Number(value)
            : null;

        setFeaCredits(normalizedValue);
        onBalanceChange?.(normalizedValue);
      })
      .catch(err => {
        if (!active) return;
        console.error('Errore recupero crediti FEA:', err);
        setCreditsError(true);
        setFeaCredits(null);
      })
      .finally(() => {
        if (active) setCreditsLoading(false);
      });

    return () => { active = false; };
  }, [token, profile?.customer_type, refreshKey]);

  const handleAssignCreditsInfo = () => {
    Swal.fire({
      icon: 'info',
      title: 'Assegna crediti firma',
      html: `
        <p>
          Puoi assegnare crediti firma tramite la piattaforma
          <strong>Consultala</strong>, nella sezione
          <strong>Firma Documenti</strong>.
        </p>
        <p style="margin-top: 12px;">
          <strong>Attenzione:</strong> una volta assegnati i crediti
          all'azienda, non potranno essere riportati sul proprio credito
          Consultala, ma resteranno di proprietà dell'azienda HR.
        </p>
      `,
      confirmButtonText: 'Ho capito',
      confirmButtonColor: '#4f46e5',
      allowOutsideClick: false,
    });
  };

  const handleRequestCreditsInfo = () => {
    Swal.fire({
      icon: 'info',
      title: 'Richiedi crediti firma',
      text: 'Puoi chiedere al tuo consulente di aggiungere firme da caricare sul tuo profilo.',
      confirmButtonText: 'Ho capito',
      confirmButtonColor: '#4f46e5',
      allowOutsideClick: false,
    });
  };

  const buttonClass = 'inline-flex items-center justify-center gap-2 rounded-md border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition-colors hover:border-indigo-300 hover:bg-indigo-100';

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="border-l-2 border-indigo-200 pl-4">
        <div className="text-xs font-bold uppercase text-indigo-700">
          Firme FEA disponibili
        </div>
        <div className="mt-1 text-lg font-bold text-indigo-900">
          {creditsLoading
            ? 'Caricamento...'
            : creditsError || feaCredits === null
              ? 'Non disponibili'
              : feaCredits}
        </div>
      </div>

      {!isEmployee && isCompanyUser && profile?.can_purchase === true && (
        <button
          type="button"
          onClick={onBuyCredits}
          disabled={!onBuyCredits}
          className={buttonClass}
        >
          <ShoppingCartOutlinedIcon fontSize="small" />
          Acquista firme
        </button>
      )}

      {!isEmployee && isConsultantUser && profile?.can_purchase === true && (
        <button
          type="button"
          onClick={handleAssignCreditsInfo}
          className={buttonClass}
        >
          <ShoppingCartOutlinedIcon fontSize="small" />
          Assegna crediti
        </button>
      )}

      {!isEmployee && isCompanyUser && profile?.can_purchase === false && (
        <button
          type="button"
          onClick={handleRequestCreditsInfo}
          className={buttonClass}
        >
          <ShoppingCartOutlinedIcon fontSize="small" />
          Richiedi crediti
        </button>
      )}
    </div>
  );
}
